import React, { useState, useEffect, useRef } from 'react';
import "../../css/viewer.css";

const ImgViewer = ({ title, images, initialIndex, closeViewer }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const imgRef = useRef(null);

  /**
  * @description 이전 이미지
  */
  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : images.length - 1));
    setZoom(1);
  };

  /**
  * @description 이후 이미지
  */
  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prevIndex) => (prevIndex < images.length - 1 ? prevIndex + 1 : 0));
    setZoom(1);
  };

  /**
  * @description 확대 버튼
  */
  const handleZoomIn = () => {
    setZoom((prevZoom) => prevZoom * 1.1);
  };

  /**
  * @description 축소 버튼
  */
  const handleZoomOut = () => {
    setZoom((prevZoom) => prevZoom / 1.1);
  };

  /**
  * @description 이미지 회전
  */
  const handleClockWise = () => {
    setRotation((prevRotation) => prevRotation + 90);
  };

  /**
  * @description 뷰어 닫기
  */
  const handleClose = () => {
    closeViewer(true);
  };

  /**
  * @description 이미지 전체보기
  */
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      imgRef.current.requestFullscreen();
      setRotation(0);
    } else {
      document.exitFullscreen();
    }
  };
  /**
  * @description 이미지 전체보기에서 클릭 또는 터치시 전체보기 해제
  */
  useEffect(() => {
    const handleClick = () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    };

    const img = imgRef.current;
    if (img) {
      img.addEventListener("click", handleClick);
      img.addEventListener("touchstart", handleClick);
    }

    return () => {
      if (img) {
        img.removeEventListener("click", handleClick);
        img.removeEventListener("touchstart", handleClick);
      }
    };
  }, []);

  /**
  * @description 이미지 본문 보기
  */
  const goDetail = () => {
    const imageId = `image-${currentIndex}`;
    const element = document.getElementById(imageId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setTimeout(() => {
        const element = document.getElementById(imageId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        handleClose();
      }, 100); 
    } else {
      handleClose();
    }
  };
  
  /**
  * @description 전체화면 상태 추적 → 해제 시 zoom 리셋
  */
  useEffect(() => {
    const exitHandler = () => {
      if (!document.fullscreenElement) {
        setZoom(1);
      }
    };

    document.addEventListener('fullscreenchange', exitHandler);
    return () => {
      document.removeEventListener('fullscreenchange', exitHandler);
    };
  }, []);

  /**
  * @description 휠 스크롤로 이미지 확대, 축소
  */
  useEffect(() => {
    const imgContainer = document.querySelector('.img-viewer-content');
    if (!imgContainer) return;
  
    const wheelHandler = (e) => {
      if (!isHovered) return;
      e.preventDefault();
      setZoom((prevZoom) => {
        const newZoom = prevZoom + e.deltaY * -0.01;
        return Math.min(Math.max(newZoom, 0.5), 3);
      });
    };
  
    imgContainer.addEventListener('wheel', wheelHandler, { passive: false });
  
    return () => {
      imgContainer.removeEventListener('wheel', wheelHandler);
    };
  }, [isHovered]);
  

  return (
    <div className="img-viewer">
      <div className="img-viewer-header">
        <div className='content-title'>{title}</div>
        <button className="img-icon img-close" onClick={handleClose}><span className="sound_only">이미지 닫기</span></button>
      </div>
      <p className='img-counter'>{currentIndex + 1} / {images.length}</p>
      <div className="img-viewer-content" 
            onMouseEnter={() => setIsHovered(true)} 
            onMouseLeave={() => setIsHovered(false)} 
        onClick={handleClose}
        >
        <img
          ref={imgRef}
          src={images[currentIndex]}
          alt="현재 이미지"
          style={{
            transform: `rotate(${rotation}deg) scale(${zoom})`, 
            transition: "transform 0.3s", 
          }}
          onClick={(e) => {e.stopPropagation();}}
        />
        <button className="img-icon img-left" onClick={handlePrev}><span className="sound_only">이전 이미지</span></button>
        <button className="img-icon img-right" onClick={handleNext}><span className="sound_only">이후 이미지</span></button>
      </div>
      <div className="toolbar">
        <button className="img-icon zoom-in" onClick={handleZoomIn}><span className="sound_only">이미지 확대</span></button>
        <button className="img-icon zoom-out" onClick={handleZoomOut}><span className="sound_only">이미지 축소</span></button>
        <button className="img-icon full-screen" onClick={toggleFullScreen}><span className="sound_only">이미지 전체보기</span></button>
        <button className="img-icon img-clockwise" onClick={handleClockWise}><span className="sound_only">이미지 시계방향 회전</span></button>
        <button className="img-icon content-text" onClick={goDetail}><span className="sound_only">이미지 위치로 글 이동</span></button>
      </div>
      <div className="img-list">
        <h3 className="sound_only">이미지 리스트</h3>
        {images.map((image, index) => (
          <img
            key={index}
            src={image}
            alt={`이미지 ${index + 1}`}
            onClick={() => setCurrentIndex(index)}
            className={index === currentIndex ? 'active' : ''}
          />
        ))}
      </div>
    </div>
  );  
};

export default ImgViewer;
